import { useEffect, useState } from "react";
import { usePostsStore } from "@/stores/posts";
import { MobilePostCard } from "./MobilePostCard";
import { Loader2 } from "lucide-react";

interface MobilePostListProps {
  className?: string;
}

export function MobilePostList({ className }: MobilePostListProps) {
  const { posts, currentPage, totalPages, isLoading, error, fetchPosts } =
    usePostsStore();

  const [localPosts, setLocalPosts] = useState<
    {
      id: string;
      content: string;
      media?: string[];
      tag?: string;
    }[]
  >([]);

  useEffect(() => {
    if (posts.length === 0) {
      fetchPosts();
    }
    // Lấy post từ localStorage
    const local = JSON.parse(localStorage.getItem("locuno_posts") || "[]");
    setLocalPosts(local);
  }, [fetchPosts, posts.length]);

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      fetchPosts(currentPage + 1);
    }
  };

  const handleComment = (postId: string) => {
    // TODO: Implement comment modal/drawer
    console.log("Comment on post:", postId);
  };

  const handleShare = (postId: string) => {
    // TODO: Implement share functionality
    console.log("Share post:", postId);
  };

  // Map local post về format phù hợp với MobilePostCard
  const mapLocalPost = (post: {
    id: string;
    content: string;
    media?: string[];
    tag?: string;
  }) => {
    let category: "regular" | "alert" | "help" = "regular";
    switch (post.tag) {
      case "Cần":
        category = "help";
        break;
      case "Nhờ":
        category = "alert";
        break;
      case "Chia sẻ":
        category = "regular";
        break;
      case "Sự kiện":
        category = "regular";
        break;
      case "Cảnh báo":
        category = "alert";
        break;
      case "Cho":
        category = "regular";
        break;
      default:
        category = "regular";
    }
    return {
      id: post.id,
      author: {
        id: "local-user",
        username: "local",
        display_name: "Bạn",
        avatar_url: "/avatars/kiki.svg",
        is_staff: false,
        is_verified: false,
        location: "Local",
      },
      community: null,
      type: "regular" as const,
      category,
      title: null,
      content: post.content,
      location: "Local",
      status: "active",
      media: post.media?.map((url: string) => ({
        type: "image" as const,
        url,
      })),
      tags: [],
      is_featured: false,
      is_edited: false,
      edited_at: null,
      view_count: 0,
      comments_count: 0,
      reactions_count: 0,
      participants_count: 0,
      is_liked: false,
      latest_comments: [],
      media_preview: [],
      createdAt: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      start_date: null,
      end_date: null,
      max_participants: null,
      helper: null,
      due_date: null,
    };
  };

  // Callback xóa bài local
  const handleLocalDelete = (id: string) => {
    setLocalPosts((prev) => prev.filter((post) => post.id !== id));
  };

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        <p>{error}</p>
        <button onClick={() => fetchPosts()} className="mt-2 text-sm underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Hiển thị post localStorage lên đầu */}
        {localPosts.map((post) => (
          <MobilePostCard
            key={post.id}
            post={mapLocalPost(post)}
            onComment={handleComment}
            onShare={handleShare}
            onLocalDelete={handleLocalDelete}
          />
        ))}
        {/* Hiển thị post từ store như cũ */}
        {posts.map((post) => (
          <MobilePostCard
            key={post.id}
            post={post}
            onComment={handleComment}
            onShare={handleShare}
          />
        ))}
      </div>

      {isLoading && (
        <div className="flex justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#215E60]" />
        </div>
      )}

      {!isLoading && currentPage < totalPages && (
        <div className="flex justify-center p-4">
          <button
            onClick={handleLoadMore}
            className="text-sm text-[#215E60] hover:text-[#1E3A3A] transition-colors"
          >
            Load more posts
          </button>
        </div>
      )}

      {!isLoading && posts.length === 0 && localPosts.length === 0 && (
        <div className="p-8 text-center text-[#6F6F6B]">
          <p>No posts yet</p>
        </div>
      )}
    </div>
  );
}
